// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faXmark,
    faPlay,
    faPause,
    faClock,
    faClosedCaptioning,
    faSearch,
    faCopy,
    faCheck,
    faPlus,
    faDatabase,
    faSpinner,
    faSync,
    faInfoCircle,
    faSave,
    faEdit
} from '@fortawesome/free-solid-svg-icons';
import { getYouTubeVideoTranscript, approveYouTubeVideo, fetchYouTubeTranscript, getApiBaseUrl, getYouTubeVideos, getYouTubeTranscriptionStatus, retranscribeYouTubeVideo } from '../services/api';
import { toast } from 'react-toastify';
import { transcriptionStateManager } from '../services/transcriptionStateManager';
import { useImpersonation } from '../contexts/ImpersonationContext';

interface TranscriptSegment {
    text: string;
    start: number;
    duration: number;
    speaker?: string;  // Speaker label from AssemblyAI
}

// Add global types
declare global {
    interface Window {
        YT: any;
        onYouTubeIframeAPIReady: () => void;
    }
}

interface TranscriptViewerProps {
    video: {
        id: number;
        video_id: string;
        title: string;
        video_url: string;
        status?: 'pending_transcription' | 'pending_review' | 'approved' | 'processing' | 'failed' | 'completed';
        actual_status?: string;
    };
    clientId: string;
    onClose: () => void;
    onAddToKnowledgeBase?: () => void;
}

const TranscriptViewer: React.FC<TranscriptViewerProps> = ({ video, clientId, onClose, onAddToKnowledgeBase }) => {
    const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isAddingToKB, setIsAddingToKB] = useState(false);
    const [isFetchingTranscript, setIsFetchingTranscript] = useState(false);
    const [transcriptionProgress, setTranscriptionProgress] = useState('');
    const [isReTranscribing, setIsReTranscribing] = useState(false);
    const [isManualMode, setIsManualMode] = useState(false);
    const [manualTranscript, setManualTranscript] = useState('');
    const [isSavingManual, setIsSavingManual] = useState(false);
    const [isInternalTranscribing, setIsInternalTranscribing] = useState(false);
    const playerRef = useRef<any>(null);
    const transcriptRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    
    // Get impersonation context
    const { impersonationState } = useImpersonation();
    
    // Check if user is super user (either directly or when impersonating)
    const originalRole = localStorage.getItem('original_user_role');
    const currentUserRole = localStorage.getItem('user_role');
    const isSuperUserImpersonating = impersonationState.isImpersonating && originalRole === 'super';
    const isSuperUser = currentUserRole === 'super' || originalRole === 'super';
    
    // Check if we're in development environment
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    useEffect(() => {
        const initializeTranscript = async () => {
            try {
                // First check server-side transcription status
                const serverStatus = await getYouTubeTranscriptionStatus(clientId, video.id);
                console.log('Server transcription status:', serverStatus);
                
                if (serverStatus.is_transcribing) {
                    // Server is actively transcribing - connect to stream
                    console.log('Server is transcribing, connecting to stream...');
                    setIsFetchingTranscript(true);
                    setLoading(false);
                    
                    // Check for any cached segments from previous connection
                    const existingState = transcriptionStateManager.getState(clientId, video.id);
                    if (existingState && existingState.segments) {
                        setTranscript(existingState.segments);
                    }
                    
                    // Connect to the transcription stream
                    connectToTranscriptionStream();
                } else {
                    // Check local state for any cached data
                    const existingState = transcriptionStateManager.getState(clientId, video.id);
                    if (existingState && existingState.segments && existingState.segments.length > 0) {
                        console.log('Using cached segments from previous session');
                        setTranscript(existingState.segments);
                        setLoading(false);
                    } else {
                        // No active transcription - check if transcript exists
                        checkTranscriptStatus();
                    }
                }
            } catch (error) {
                console.error('Error checking transcription status:', error);
                // Fallback to normal transcript check
                checkTranscriptStatus();
            }
        };
        
        initializeTranscript();
        loadYouTubeAPI();
        
        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            // Don't clear the transcription state on unmount - keep it for when modal reopens
        };
    }, [video.id]);
    
    const checkTranscriptStatus = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Only check database for existing transcript - don't fetch from YouTube
            const response = await getYouTubeVideoTranscript(clientId, video.id);
            
            console.log('Transcript response:', response);
            
            if (response.transcript && response.transcript.segments) {
                setTranscript(response.transcript.segments);
                setLoading(false);
            } else if (response.status === 'processing') {
                // Transcription is in progress, connect to stream
                setLoading(false);
                setError('');
                setTranscript([]); // Clear any old transcript
                setIsFetchingTranscript(true); // Show that we're fetching
                connectToTranscriptionStream();
            } else {
                // No transcript in database - don't auto-fetch from YouTube
                setLoading(false);
                // Don't show error for pending_transcription videos
                if (video.status !== 'pending_transcription') {
                    setError('No transcript available for this video');
                }
            }
        } catch (err: any) {
            console.error('Error fetching transcript:', err);
            setLoading(false);
            
            // If 404 or no transcript, that's ok
            if (err.response?.status === 404 || err.message?.includes('not found')) {
                // Don't set error for pending_transcription videos
                if (video.status !== 'pending_transcription') {
                    setError('No transcript available for this video');
                }
            } else {
                // Show the actual error
                setError(err.message || 'Failed to load transcript');
            }
        }
    };
    
    const connectToTranscriptionStream = () => {
        // Connect to the streaming endpoint to get real-time updates
        handleFetchTranscript();
    };


    const loadYouTubeAPI = () => {
        if (window.YT) {
            setTimeout(() => {
                initializePlayer();
            }, 100);
            return;
        }

        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        if (firstScriptTag && firstScriptTag.parentNode) {
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } else {
            document.head.appendChild(tag);
        }

        window.onYouTubeIframeAPIReady = () => {
            setTimeout(() => {
                initializePlayer();
            }, 100);
        };
    };

    const initializePlayer = () => {
        if (!window.YT || !window.YT.Player) return;

        // Check if the player div exists
        const playerDiv = document.getElementById('youtube-player');
        if (!playerDiv) {
            console.error('YouTube player div not found, retrying...');
            setTimeout(() => {
                initializePlayer();
            }, 500);
            return;
        }

        try {
            playerRef.current = new window.YT.Player('youtube-player', {
                height: '100%',
                width: '100%',
                videoId: video.video_id,
                playerVars: {
                    autoplay: 0,
                    modestbranding: 1,
                    rel: 0
                },
                events: {
                    onReady: onPlayerReady,
                    onStateChange: onPlayerStateChange
                }
            });
        } catch (error) {
            console.error('Error initializing YouTube player:', error);
        }
    };

    const onPlayerReady = (event: any) => {
        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        
        // Update time periodically when playing
        intervalRef.current = setInterval(() => {
            if (playerRef.current && typeof playerRef.current.getPlayerState === 'function') {
                try {
                    if (playerRef.current.getPlayerState() === 1) {
                        setCurrentTime(playerRef.current.getCurrentTime());
                        scrollToCurrentSegment();
                    }
                } catch (e) {
                    // Player not ready yet
                }
            }
        }, 1000);
    };

    const onPlayerStateChange = (event: any) => {
        setIsPlaying(event.data === 1);
    };


    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const seekToTime = (time: number) => {
        if (playerRef.current && playerRef.current.seekTo) {
            playerRef.current.seekTo(time, true);
            setCurrentTime(time);
            // Always play the video after seeking
            setTimeout(() => {
                if (playerRef.current && playerRef.current.playVideo) {
                    playerRef.current.playVideo();
                }
            }, 100);
        }
    };

    const copyTranscript = () => {
        // Format transcript with timestamps in [MM:SS] format
        const formattedTranscript = transcript.map(seg => {
            const minutes = Math.floor(seg.start / 60);
            const seconds = Math.floor(seg.start % 60);
            const timestamp = `[${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}]`;
            return `${timestamp} ${seg.text}`;
        }).join('\n');
        
        navigator.clipboard.writeText(formattedTranscript);
        setCopied(true);
        toast.success('Transcript copied with timestamps in [MM:SS] format');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleAddToKnowledgeBase = async () => {
        if (transcript.length === 0) {
            toast.error('No transcript available to add to knowledge base');
            return;
        }
        
        try {
            setIsAddingToKB(true);
            await approveYouTubeVideo(clientId, video.id);
            
            // Don't close immediately - show processing state
            toast.info('Video approved! Processing and adding to knowledge base...', {
                autoClose: false,
                toastId: `kb-processing-${video.id}`
            });
            
            // Start monitoring the processing status
            let hasCompleted = false; // Flag to prevent multiple success messages
            const checkInterval = setInterval(async () => {
                try {
                    const response = await getYouTubeVideos(clientId);
                    // Check if response has videos array
                    const videos = response.data?.items || response.videos || [];
                    const currentVideo = videos.find((v: any) => v.id === video.id);
                    
                    if (currentVideo) {
                        if (currentVideo.status === 'completed' && !hasCompleted) {
                            hasCompleted = true; // Set flag before clearing interval
                            clearInterval(checkInterval);
                            toast.dismiss(`kb-processing-${video.id}`);
                            toast.success('Video successfully added to knowledge base!');
                            if (onAddToKnowledgeBase) {
                                onAddToKnowledgeBase();
                            }
                            onClose();
                        } else if (currentVideo.status === 'failed') {
                            clearInterval(checkInterval);
                            toast.dismiss(`kb-processing-${video.id}`);
                            toast.error('Failed to add video to knowledge base');
                            setIsAddingToKB(false);
                        }
                        // Continue checking if status is 'processing'
                    }
                } catch (error) {
                    console.error('Error checking processing status:', error);
                }
            }, 2000); // Check every 2 seconds
            
            // Set a timeout to prevent infinite checking
            setTimeout(() => {
                clearInterval(checkInterval);
                toast.dismiss(`kb-processing-${video.id}`);
                if (isAddingToKB) {
                    toast.warning('Knowledge base processing is taking longer than expected. You can close this window.');
                    setIsAddingToKB(false);
                }
            }, 120000); // 2 minute timeout
            
        } catch (error) {
            console.error('Error adding to knowledge base:', error);
            toast.error('Failed to add video to knowledge base');
            setIsAddingToKB(false);
        }
    };

    const handleReTranscribe = async () => {
        try {
            setIsReTranscribing(true);
            
            // First call the API to reset the video status
            await retranscribeYouTubeVideo(clientId, video.id);
            
            // Clear existing transcript
            setTranscript([]);
            transcriptionStateManager.clearState(clientId, video.id);
            
            // Update the video status locally
            if (onAddToKnowledgeBase) {
                onAddToKnowledgeBase(); // This will refresh the video list
            }
            
            toast.info('Video reset for re-transcription. Status changed to "Pending Review".');
            
            // Start fetching new transcript
            handleFetchTranscript();
            setIsReTranscribing(false);
            
        } catch (error) {
            console.error('Error re-transcribing:', error);
            toast.error('Failed to start re-transcription');
            setIsReTranscribing(false);
        }
    };

    const handleInternalTranscription = async () => {
        // This is the new internal transcription flow that tries YouTube first, then falls back to Whisper
        setIsInternalTranscribing(true);
        handleFetchTranscript();
    };

    const handleDirectWhisperTranscription = async () => {
        // Direct Whisper transcription - bypasses YouTube completely
        try {
            setIsInternalTranscribing(true);
            setIsFetchingTranscript(true); // Enable progress UI
            setError('');
            
            // Clear existing transcript to show we're re-transcribing
            setTranscript([]);
            transcriptionStateManager.clearState(clientId, video.id);
            
            // Use streaming endpoint with direct_whisper parameter
            const url = `${getApiBaseUrl()}/youtube/video/${clientId}/${video.id}/transcribe-stream?direct_whisper=true`;
            
            const eventSource = new EventSource(url);
            
            console.log('[TranscriptViewer] Created EventSource for direct Whisper URL:', url);
            
            // Save the EventSource and mark as processing
            transcriptionStateManager.setState(clientId, video.id, {
                isProcessing: true,
                eventSource: eventSource,
                segments: []
            });
            
            let progressMessage = 'Starting direct Whisper transcription...';
            let allSegments: TranscriptSegment[] = [];
            
            eventSource.onopen = () => {
                console.log('[TranscriptViewer] Direct Whisper EventSource connection opened');
            };
            
            eventSource.onmessage = (event) => {
                console.log('[TranscriptViewer] Received SSE message:', event.data);
                const update = JSON.parse(event.data);
                
                switch (update.type) {
                    case 'status':
                        progressMessage = update.message || progressMessage;
                        setTranscriptionProgress(progressMessage);
                        setError(''); // Clear error to show progress
                        console.log('[TranscriptViewer] Status update:', progressMessage);
                        break;
                        
                    case 'chunk_start':
                        progressMessage = `Processing chunk ${update.chunk}/${update.total_chunks} (${update.time_range})`;
                        setTranscriptionProgress(progressMessage);
                        break;
                        
                    case 'chunk_complete':
                        progressMessage = `Completed chunk ${update.chunk}/${update.total_chunks}`;
                        setTranscriptionProgress(progressMessage);
                        break;
                        
                    case 'segments_available':
                        if (update.all_segments) {
                            allSegments = update.all_segments;
                            setTranscript(allSegments);
                            setLoading(false);
                            
                            transcriptionStateManager.setState(clientId, video.id, {
                                segments: allSegments
                            });
                            
                            requestAnimationFrame(() => {
                                setTimeout(() => {
                                    if (transcriptRef.current) {
                                        const container = transcriptRef.current;
                                        container.scrollTo({
                                            top: container.scrollHeight,
                                            behavior: 'smooth'
                                        });
                                    }
                                }, 150);
                            });
                        }
                        progressMessage = update.message || `Transcribed ${allSegments.length} segments`;
                        setTranscriptionProgress(progressMessage);
                        break;
                        
                    case 'complete':
                        eventSource.close();
                        setIsInternalTranscribing(false);
                        setIsFetchingTranscript(false);
                        
                        transcriptionStateManager.setState(clientId, video.id, {
                            isProcessing: false,
                            segments: allSegments
                        });
                        
                        let message = 'Direct Whisper transcript completed!';
                        if (update.cost) {
                            message += ` (Cost: $${update.cost.toFixed(4)})`;
                        }
                        toast.success(message);
                        
                        setTimeout(() => {
                            checkTranscriptStatus();
                        }, 1000);
                        break;
                        
                    case 'error':
                        eventSource.close();
                        setIsInternalTranscribing(false);
                        setIsFetchingTranscript(false);
                        setError(update.message || 'Transcription failed');
                        toast.error(update.message || 'Transcription failed');
                        transcriptionStateManager.clearState(clientId, video.id);
                        break;
                        
                    case 'end':
                        eventSource.close();
                        setIsInternalTranscribing(false);
                        setIsFetchingTranscript(false);
                        transcriptionStateManager.setState(clientId, video.id, {
                            isProcessing: false
                        });
                        break;
                }
            };
            
            eventSource.onerror = (error) => {
                console.error('[TranscriptViewer] EventSource error:', error);
                eventSource.close();
                setIsInternalTranscribing(false);
                setIsFetchingTranscript(false);
                setError('Connection lost during transcription');
                transcriptionStateManager.setState(clientId, video.id, {
                    isProcessing: false
                });
            };
            
        } catch (error) {
            console.error('Error starting direct Whisper transcription:', error);
            toast.error('Failed to start direct Whisper transcription');
            setIsInternalTranscribing(false);
            setIsFetchingTranscript(false);
        }
    };

    const handleFetchTranscript = async () => {
        try {
            setIsFetchingTranscript(true);
            setError('');
            
            // Get existing segments if any
            const existingSegments = transcriptionStateManager.getSegments(clientId, video.id);
            setTranscript(existingSegments);
            
            // Use streaming endpoint for real-time updates
            const url = `${getApiBaseUrl()}/youtube/video/${clientId}/${video.id}/transcribe-stream`;
            
            const eventSource = new EventSource(url);
            
            console.log('[TranscriptViewer] Created EventSource for URL:', url);
            
            // Save the EventSource and mark as processing
            transcriptionStateManager.setState(clientId, video.id, {
                isProcessing: true,
                eventSource: eventSource,
                segments: existingSegments
            });
            
            let progressMessage = 'Starting transcription...';
            let allSegments: TranscriptSegment[] = existingSegments;
            
            eventSource.onopen = () => {
                console.log('[TranscriptViewer] EventSource connection opened');
            };
            
            eventSource.onmessage = (event) => {
                console.log('[TranscriptViewer] Received SSE message:', event.data);
                const update = JSON.parse(event.data);
                
                switch (update.type) {
                    case 'status':
                        progressMessage = update.message || progressMessage;
                        setTranscriptionProgress(progressMessage);
                        // Update the UI to show status
                        setError(''); // Clear error to show progress
                        console.log('[TranscriptViewer] Status update:', progressMessage);
                        break;
                        
                    case 'chunk_start':
                        progressMessage = `Processing chunk ${update.chunk}/${update.total_chunks} (${update.time_range})`;
                        setTranscriptionProgress(progressMessage);
                        break;
                        
                    case 'chunk_complete':
                        progressMessage = `Completed chunk ${update.chunk}/${update.total_chunks}`;
                        setTranscriptionProgress(progressMessage);
                        break;
                        
                    case 'segments_available':
                        // NEW SEGMENTS! Show them immediately
                        if (update.all_segments) {
                            allSegments = update.all_segments;
                            setTranscript(allSegments);
                            setLoading(false); // Hide loader once we have segments
                            
                            // Save segments to state manager
                            transcriptionStateManager.setState(clientId, video.id, {
                                segments: allSegments
                            });
                            
                                            // Auto-scroll to show new segments with better timing
                            requestAnimationFrame(() => {
                                setTimeout(() => {
                                    if (transcriptRef.current) {
                                        const container = transcriptRef.current;
                                        // Smooth scroll to bottom to show new segments
                                        container.scrollTo({
                                            top: container.scrollHeight,
                                            behavior: 'smooth'
                                        });
                                    }
                                }, 150);
                            });
                        }
                        progressMessage = update.message || `Transcribed ${allSegments.length} segments`;
                        setTranscriptionProgress(progressMessage);
                        break;
                        
                    case 'complete':
                        eventSource.close();
                        setIsFetchingTranscript(false);
                        setIsInternalTranscribing(false); // Reset internal transcribing state
                        
                        // Mark as completed in state manager
                        transcriptionStateManager.setState(clientId, video.id, {
                            isProcessing: false,
                            segments: allSegments
                        });
                        
                        // Show success message
                        let message = 'Transcript completed!';
                        if (update.method === 'youtube_subtitles') {
                            message = 'YouTube subtitles loaded!';
                        } else if (update.cost) {
                            message += ` (Cost: $${update.cost.toFixed(4)})`;
                        }
                        toast.success(message);
                        
                        // Reload the transcript to ensure it's saved in DB
                        setTimeout(() => {
                            checkTranscriptStatus();
                        }, 1000);
                        break;
                        
                    case 'error':
                        eventSource.close();
                        setIsFetchingTranscript(false);
                        setIsInternalTranscribing(false); // Reset internal transcribing state
                        setError(update.message || 'Transcription failed');
                        toast.error(update.message || 'Transcription failed');
                        
                        // Clear processing state on error
                        transcriptionStateManager.clearState(clientId, video.id);
                        break;
                        
                    case 'end':
                        eventSource.close();
                        setIsFetchingTranscript(false);
                        setIsInternalTranscribing(false); // Reset internal transcribing state
                        
                        // Mark as completed
                        transcriptionStateManager.setState(clientId, video.id, {
                            isProcessing: false
                        });
                        break;
                }
            };
            
            eventSource.onerror = (error) => {
                console.error('[TranscriptViewer] EventSource error:', error);
                console.log('[TranscriptViewer] EventSource readyState:', eventSource.readyState);
                
                if (eventSource.readyState === EventSource.CLOSED) {
                    console.log('[TranscriptViewer] EventSource connection closed');
                    setIsFetchingTranscript(false);
                    setIsInternalTranscribing(false); // Reset internal transcribing state
                    setError('Connection lost during transcription');
                    
                    // Keep the segments but mark as not processing
                    transcriptionStateManager.setState(clientId, video.id, {
                        isProcessing: false
                    });
                } else {
                    console.log('[TranscriptViewer] EventSource error but connection still open');
                }
            };
            
            // Show progress in UI while processing
            const progressInterval = setInterval(() => {
                if (isFetchingTranscript && document.querySelector('.transcript-progress')) {
                    const progressEl = document.querySelector('.transcript-progress');
                    if (progressEl) {
                        progressEl.textContent = progressMessage;
                    }
                }
            }, 100);
            
            // Cleanup interval when done
            eventSource.addEventListener('close', () => {
                clearInterval(progressInterval);
            });
            
        } catch (error) {
            console.error('Error starting transcription:', error);
            toast.error('Failed to start transcription');
            setIsFetchingTranscript(false);
            setIsInternalTranscribing(false); // Reset internal transcribing state
        }
    };

    const highlightSearchTerm = (text: string) => {
        if (!searchTerm) return text;
        
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        return text.split(regex).map((part, index) => 
            regex.test(part) ? <mark key={index} className="bg-yellow-200">{part}</mark> : part
        );
    };
    
    // Parse manual transcript input
    const parseManualTranscript = (input: string): TranscriptSegment[] => {
        const lines = input.split('\n').filter(line => line.trim());
        const segments: TranscriptSegment[] = [];
        
        for (const line of lines) {
            // Support formats: "[00:00] text" or "00:00 - text" or "00:00 text"
            const match = line.match(/^\[?(\d{1,2}):(\d{2})\]?\s*[-]?\s*(.+)$/);
            if (match) {
                const minutes = parseInt(match[1]);
                const seconds = parseInt(match[2]);
                const text = match[3].trim();
                const start = minutes * 60 + seconds;
                
                // Calculate duration based on next segment or default to 5 seconds
                const duration = 5;
                
                segments.push({
                    text,
                    start,
                    duration
                });
            }
        }
        
        // Adjust durations based on next segment start times
        for (let i = 0; i < segments.length - 1; i++) {
            segments[i].duration = segments[i + 1].start - segments[i].start;
        }
        
        return segments;
    };
    
    // Save manual transcript
    const handleSaveManualTranscript = async () => {
        const parsedSegments = parseManualTranscript(manualTranscript);
        
        if (parsedSegments.length === 0) {
            toast.error('No valid transcript segments found. Please use format: [00:00] Text');
            return;
        }
        
        try {
            setIsSavingManual(true);
            
            const headers: any = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            };
            
            // Add impersonation headers if active
            if (localStorage.getItem('impersonation_token')) {
                headers['X-Impersonation-Active'] = 'true';
                // Add original user role for super user check
                const originalRole = localStorage.getItem('original_user_role');
                if (originalRole) {
                    headers['X-Original-User-Role'] = originalRole;
                }
            }
            
            const response = await fetch(`${getApiBaseUrl()}/youtube/video/${clientId}/${video.id}/manual-transcript`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    segments: parsedSegments
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to save manual transcript');
            }
            
            // Update local state
            setTranscript(parsedSegments);
            setIsManualMode(false);
            setError('');
            toast.success('Manual transcript saved successfully!');
            
            // Refresh the transcript status
            checkTranscriptStatus();
            
        } catch (error) {
            console.error('Error saving manual transcript:', error);
            toast.error('Failed to save manual transcript');
        } finally {
            setIsSavingManual(false);
        }
    };

    const scrollToCurrentSegment = () => {
        if (!transcriptRef.current) return;
        
        const currentSegment = transcript.findIndex(seg => 
            currentTime >= seg.start && currentTime < seg.start + seg.duration
        );
        
        if (currentSegment >= 0) {
            const element = transcriptRef.current.querySelector(`[data-segment="${currentSegment}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    const isSegmentActive = (segment: TranscriptSegment) => {
        return currentTime >= segment.start && currentTime < segment.start + segment.duration;
    };

    const filteredTranscript = transcript.filter(seg => 
        !searchTerm || seg.text.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <style>{`
                @keyframes fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out forwards;
                }
            `}</style>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex-1">
                        <h2 className="text-xl font-semibold text-gray-800 truncate">
                            {video.title}
                        </h2>
                        <div className="flex items-center gap-4 mt-1">
                            <span className="text-sm text-gray-600 flex items-center gap-1">
                                <FontAwesomeIcon icon={faClosedCaptioning} />
                                Transcript
                            </span>
                            {transcript.length > 0 && (
                                <span className="text-sm text-gray-600">
                                    {transcript.length} segments
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 p-2"
                    >
                        <FontAwesomeIcon icon={faXmark} className="text-xl" />
                    </button>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Video Player - Reduced to 1/3 width */}
                    <div className="w-1/3 bg-black flex items-center justify-center p-4">
                        <div className="w-full aspect-video relative">
                            <div id="youtube-player" className="absolute inset-0"></div>
                        </div>
                    </div>

                    {/* Transcript - Increased to 2/3 width */}
                    <div className="w-2/3 flex flex-col bg-gray-50">
                        {/* Transcript Controls */}
                        <div className="p-4 bg-white border-b space-y-3">
                            <div className="relative">
                                <FontAwesomeIcon 
                                    icon={faSearch} 
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search transcript..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                                />
                            </div>
                            <div className="flex justify-end items-center">
                                <span className="text-sm text-gray-600">
                                    Current: {formatTime(currentTime)}
                                </span>
                            </div>
                        </div>

                        {/* Transcript Content */}
                        <div ref={transcriptRef} className="flex-1 overflow-y-auto overflow-x-hidden p-4 relative"
                             style={{ scrollBehavior: 'smooth' }}>
                            {loading && (
                                <div className="text-center py-8">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
                                    <p className="mt-2 text-gray-600">Checking for transcript...</p>
                                </div>
                            )}

                            {error && (
                                <div className="text-center py-8">
                                    <p className="text-red-600">{error}</p>
                                </div>
                            )}

                            {/* Show progress when transcribing but no segments yet */}
                            {isFetchingTranscript && transcript.length === 0 && (
                                <div className="text-center py-8">
                                    <FontAwesomeIcon icon={faSpinner} className="text-6xl text-brand-500 mb-4 animate-spin" />
                                    <p className="text-lg font-medium text-gray-700 mb-2">
                                        {transcriptionProgress || 'Starting transcription...'}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        This may take a few minutes depending on video length
                                    </p>
                                </div>
                            )}
                            
                            {!loading && !error && transcript.length === 0 && !isFetchingTranscript && (video.status === 'pending_transcription' || isSuperUser) && (
                                <div className="text-center py-8">
                                    <FontAwesomeIcon icon={faClosedCaptioning} className="text-6xl text-gray-300 mb-4" />
                                    <p className="text-gray-500 mb-4">
                                        No transcript available for this video
                                    </p>
                                    
                                    {isSuperUser ? (
                                        // Super user (either direct super user or impersonating)
                                        isDevelopment ? (
                                            // On local - show internal transcription button
                                            <>
                                                {!isManualMode ? (
                                                    <>
                                                        <div className="flex flex-col gap-4 items-center">
                                                            {/* Internal Transcription button - only on localhost for super users */}
                                                            <button
                                                                onClick={handleInternalTranscription}
                                                                disabled={isInternalTranscribing || isFetchingTranscript}
                                                                className="bg-brand-600 text-white px-6 py-2 rounded-lg hover:bg-brand-700 disabled:bg-gray-400 inline-flex items-center gap-2 whitespace-nowrap"
                                                            >
                                                                <FontAwesomeIcon 
                                                                    icon={(isInternalTranscribing || isFetchingTranscript) ? faSpinner : faSync} 
                                                                    className={(isInternalTranscribing || isFetchingTranscript) ? 'animate-spin' : ''} 
                                                                />
                                                                {(isInternalTranscribing || isFetchingTranscript) ? (
                                                                    <span className="transcript-progress">{transcriptionProgress || 'Starting transcription...'}</span>
                                                                ) : (
                                                                    'Internal Transcription'
                                                                )}
                                                            </button>
                                                            <div className="text-sm text-gray-400 space-y-1">
                                                                <p className="text-brand-400">✓ YouTube → Whisper fallback</p>
                                                                <p>Tries YouTube subtitles first, then our service</p>
                                                            </div>
                                                            
                                                            <div className="text-gray-400 text-sm my-2">— OR —</div>
                                                            
                                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-lg">
                                                                <FontAwesomeIcon icon={faInfoCircle} className="text-yellow-600 mb-2" />
                                                                <p className="text-sm text-gray-700 mb-3">
                                                                    Transcript not available on YouTube. You can manually add a transcript for this video.
                                                                </p>
                                                                <button
                                                                    onClick={() => setIsManualMode(true)}
                                                                    className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 inline-flex items-center gap-2 whitespace-nowrap"
                                                                >
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                    Add Manual Transcript
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    // Manual transcript entry mode
                                                    <div className="w-full max-w-2xl mx-auto text-left">
                                                        <div className="mb-4">
                                                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Add Manual Transcript</h3>
                                                            <p className="text-sm text-gray-600 mb-4">
                                                                Enter transcript with timestamps in format: [00:00] Text or 00:00 - Text
                                                            </p>
                                                            <textarea
                                                                value={manualTranscript}
                                                                onChange={(e) => setManualTranscript(e.target.value)}
                                                                placeholder="[00:00] Welcome to the video&#10;[00:05] Today we'll discuss...&#10;[00:15] First topic is..."
                                                                className="w-full h-64 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 font-sans text-sm"
                                                            />
                                                        </div>
                                                        <div className="flex justify-center gap-3">
                                                            <button
                                                                onClick={() => {
                                                                    setIsManualMode(false);
                                                                    setManualTranscript('');
                                                                }}
                                                                className="px-4 py-2 text-gray-700 hover:text-gray-900"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={handleSaveManualTranscript}
                                                                disabled={isSavingManual || !manualTranscript.trim()}
                                                                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                                                            >
                                                                <FontAwesomeIcon icon={isSavingManual ? faSpinner : faSave} className={isSavingManual ? 'animate-spin' : ''} />
                                                                {isSavingManual ? 'Saving...' : 'Save Transcript'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            // On production - show manual entry option only
                                            <>
                                                {!isManualMode ? (
                                                    <>
                                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 max-w-lg mx-auto">
                                                            <FontAwesomeIcon icon={faInfoCircle} className="text-yellow-600 mb-2" />
                                                            <p className="text-sm text-gray-700">
                                                                Transcript not available on YouTube. You can manually add a transcript for this video.
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => setIsManualMode(true)}
                                                            className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 inline-flex items-center gap-2 whitespace-nowrap"
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                            Add Manual Transcript
                                                        </button>
                                                    </>
                                                ) : (
                                                // Manual transcript entry mode
                                                <div className="w-full max-w-2xl mx-auto text-left">
                                                    <div className="mb-4">
                                                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Add Manual Transcript</h3>
                                                        <p className="text-sm text-gray-600 mb-4">
                                                            Enter transcript with timestamps in format: [00:00] Text or 00:00 - Text
                                                        </p>
                                                        <textarea
                                                            value={manualTranscript}
                                                            onChange={(e) => setManualTranscript(e.target.value)}
                                                            placeholder="[00:00] Welcome to the video&#10;[00:05] Today we'll discuss...&#10;[00:15] First topic is..."
                                                            className="w-full h-64 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 font-sans text-sm"
                                                        />
                                                    </div>
                                                    <div className="flex justify-center gap-3">
                                                        <button
                                                            onClick={() => {
                                                                setIsManualMode(false);
                                                                setManualTranscript('');
                                                            }}
                                                            className="px-4 py-2 text-gray-700 hover:text-gray-900"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={handleSaveManualTranscript}
                                                            disabled={isSavingManual || !manualTranscript.trim()}
                                                            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                                                        >
                                                            <FontAwesomeIcon icon={isSavingManual ? faSpinner : faSave} className={isSavingManual ? 'animate-spin' : ''} />
                                                            {isSavingManual ? 'Saving...' : 'Save Transcript'}
                                                        </button>
                                                    </div>
                                                </div>
                                                )}
                                            </>
                                        )
                                    ) : (
                                        // Regular user - show informative message
                                        <div className="bg-brand-50 border border-brand-200 rounded-lg p-6 max-w-lg mx-auto">
                                            <FontAwesomeIcon icon={faInfoCircle} className="text-brand-500 text-2xl mb-3" />
                                            <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                                Transcript Not Available on YouTube
                                            </h3>
                                            <p className="text-sm text-gray-700 mb-4">
                                                This video doesn't have captions or subtitles available on YouTube. Here are your options:
                                            </p>
                                            <ul className="text-sm text-gray-700 space-y-2 text-left">
                                                <li className="flex items-start">
                                                    <span className="text-brand-500 mr-2">•</span>
                                                    <span><strong>Add captions on YouTube:</strong> Enable captions/subtitles in YouTube Studio, then delete and re-sync this video to fetch the transcript</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-brand-500 mr-2">•</span>
                                                    <span><strong>Delete and re-sync:</strong> If captions were recently added on YouTube, delete this video and add it again to fetch the transcript</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-brand-500 mr-2">•</span>
                                                    <span><strong>Contact our sales team:</strong> We can help transcribe important videos for you manually</span>
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {!loading && !error && filteredTranscript.length === 0 && transcript.length > 0 && (
                                <div className="text-center py-8">
                                    <p className="text-gray-500">
                                        No matching segments found
                                    </p>
                                </div>
                            )}

                            {!loading && !error && filteredTranscript.map((segment, index) => {
                                // Show animation for the last few segments when fetching
                                const isNewSegment = isFetchingTranscript && index >= filteredTranscript.length - 3;
                                const animationDelay = isNewSegment ? (index - (filteredTranscript.length - 3)) * 0.1 : 0;
                                
                                return (
                                    <div
                                        key={index}
                                        data-segment={index}
                                        className={`mb-3 p-3 rounded-lg cursor-pointer transition-all ${
                                            isSegmentActive(segment) 
                                                ? 'bg-brand-100 border-l-4 border-brand-500' 
                                                : isNewSegment
                                                    ? 'bg-brand-50 border-l-4 border-brand-500 animate-fade-in'
                                                    : 'bg-white hover:bg-gray-100'
                                        }`}
                                        style={{
                                            animationDelay: `${animationDelay}s`,
                                            opacity: isNewSegment ? 0 : 1
                                        }}
                                        onClick={() => seekToTime(segment.start)}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-brand-500 flex items-center gap-1 whitespace-nowrap">
                                                    <FontAwesomeIcon icon={faClock} className="text-xs" />
                                                    {formatTime(segment.start)}
                                                </span>
                                                {segment.speaker && (
                                                    <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">
                                                        {segment.speaker}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-800 flex-1">
                                                {highlightSearchTerm(segment.text)}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                            
                            {/* Real-time progress indicator */}
                            {isFetchingTranscript && transcript.length > 0 && (
                                <div className="mt-4 p-3 bg-brand-50 rounded-lg border border-brand-200">
                                    <div className="flex items-center gap-2 text-brand-600">
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-sm" />
                                        <span className="text-sm font-medium transcript-progress">
                                            {transcriptionProgress || 'Processing more segments...'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Footer */}
                {transcript.length > 0 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                            <button
                                onClick={copyTranscript}
                                className="text-brand-500 hover:text-brand-600 flex items-center gap-2"
                                title="Copy transcript with timestamps in [MM:SS] format"
                            >
                                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                                {copied ? 'Copied!' : 'Copy All'}
                            </button>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 hover:text-gray-900"
                            >
                                Close
                            </button>
                            
                            {/* Show Internal Transcription button for super users on localhost */}
                            {isDevelopment && isSuperUser && (
                                <button
                                    onClick={handleDirectWhisperTranscription}
                                    disabled={isInternalTranscribing}
                                    className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                                    title="Direct Whisper transcription (bypasses YouTube)"
                                >
                                    <FontAwesomeIcon icon={isInternalTranscribing ? faSpinner : faSync} className={isInternalTranscribing ? 'animate-spin' : ''} />
                                    {isInternalTranscribing ? 'Transcribing...' : 'Internal Transcription'}
                                </button>
                            )}
                            
                            {/* Show Add to Knowledge Base button only when transcript is available */}
                            {transcript.length > 0 && (
                                <button
                                    onClick={handleAddToKnowledgeBase}
                                    disabled={isAddingToKB || isFetchingTranscript}
                                    className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                                    title={isFetchingTranscript ? 'Wait for transcription to complete' : isAddingToKB ? 'Processing video, please wait...' : video.status === 'completed' ? 'Re-add video to knowledge base' : 'Add video to knowledge base'}
                                >
                                    <FontAwesomeIcon icon={isAddingToKB ? faSpinner : faDatabase} className={isAddingToKB ? 'animate-spin' : ''} />
                                    {isAddingToKB ? 'Processing Knowledge Base...' : isFetchingTranscript ? 'Transcribing...' : 'Add to Knowledge Base'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default TranscriptViewer;